/**
 * The storage adapter supabase-js persists the session through.
 *
 * One rule, and it is the whole reason this file exists rather than passing `window.localStorage`
 * straight in:
 *
 * > **A read that fails means "no session". It must never destroy the session.**
 *
 * That is a production incident, not a hypothetical. The mobile app's encrypted store
 * (`LargeSecureStore`, header dated 2026-07-22) rotated its AES key on every write — two
 * non-atomic stores — so a crash between them left a fresh key against a stale blob. Decrypt
 * failed, and the failure path at the time escalated that into deleting the blob and forcing a
 * local sign-out. A transient race became a permanent logout, repeatedly. The fix was a stable key
 * plus this rule.
 *
 * The browser has nothing to decrypt, but it has the same shape of failure: `localStorage` throws
 * a `SecurityError` when storage is blocked (a third-party context, Safari with cookies disabled),
 * a `QuotaExceededError` on write, and is absent entirely in some embedded webviews. If any of
 * those made this adapter reach for `removeItem`, a trainer whose browser hiccuped once would be
 * signed out — and the `sessionMachine` terminality rule would be bypassed from below, because
 * supabase-js would then see an empty store and legitimately report no session.
 *
 * So: `getItem` swallows and answers `null`, and it never removes. `setItem` and `removeItem`
 * swallow too — a session that cannot be persisted still works for the life of the tab, because
 * supabase-js holds it in memory as well, and that is strictly better than a thrown error inside
 * its own state machine.
 *
 * What it deliberately does NOT do: keep an in-memory shadow copy that answers when the backing
 * store returns `null`. A `null` from a working store is how a sign-out in another tab arrives
 * (§6, shared `localStorage`), and shadowing it would resurrect a session the trainer just ended.
 * The in-memory map is used only when there is no backing store at all.
 *
 * Pure by construction: the backing store is a PARAMETER. This module never touches `window` and
 * imports nothing from supabase-js, so a test drives every failure mode by passing a store that
 * throws. The returned shape is structurally supabase-js's `SupportedStorage`, and
 * `supabaseClient.ts` proves that where it hands it to `createClient`.
 */

/** The three methods supabase-js uses. Matches the shape of `window.localStorage`. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Wrap a key-value store so no failure inside it can end a session.
 *
 * `backing` is `null` when the environment has no storage at all. The session then lives for the
 * life of the tab only, which is the honest outcome — the alternative is refusing to sign in.
 */
export function createResilientSessionStorage(backing: KeyValueStore | null): KeyValueStore {
  if (!backing) {
    const memory = new Map<string, string>();
    return {
      getItem: (key) => memory.get(key) ?? null,
      setItem: (key, value) => void memory.set(key, value),
      removeItem: (key) => void memory.delete(key),
    };
  }

  return {
    getItem(key) {
      try {
        return backing.getItem(key);
      } catch {
        // Deliberately NOT `removeItem(key)`. "I could not read it" is not "it is not there".
        return null;
      }
    },

    setItem(key, value) {
      try {
        backing.setItem(key, value);
      } catch {
        // Quota, or a blocked store. supabase-js still holds the session in memory, so this tab
        // keeps working; the next cold start simply finds nothing, which is the truth.
      }
    },

    removeItem(key) {
      try {
        backing.removeItem(key);
      } catch {
        // Logout is the one flow that must never be blockable by anything.
      }
    },
  };
}
